import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { apiClient } from "@/lib/api";
import { SITE_URL, SITE_BRAND } from "@/lib/seo-config";
import { PERSONA_TYPES } from "@/lib/persona-types";
import CityProspectForm from "@/components/CityProspectForm";
import { ArrowRight, Lock, Camera, BadgeCheck, Handshake } from "lucide-react";

function buildCrossSEO(persona, city, cityLabel) {
  return {
    title: `${SITE_BRAND} pour ${persona.label.toLowerCase()} à ${cityLabel}`,
    description: `${persona.label} à ${cityLabel} : ${persona.seo.description.slice(0, 180)}`,
    keywords: `${persona.label.toLowerCase()} ${cityLabel}, ${persona.seo.keywords.split(",").slice(0, 5).join(", ")}, ${persona.label.toLowerCase()} sécurisation travaux ${cityLabel}, Batisur ${cityLabel}`,
    canonical: `/pour/${persona.slug}/${city}`,
    ogType: "website",
    schema: [
      {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": `${SITE_BRAND} pour ${persona.label} à ${cityLabel}`,
        "description": persona.seo.description,
        "provider": { "@type": "Organization", "name": SITE_BRAND, "url": SITE_URL },
        "areaServed": { "@type": "City", "name": cityLabel },
        "audience": {
          "@type": persona.slug === "particulier" ? "PeopleAudience" : "BusinessAudience",
          "audienceType": persona.label,
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "name": `${SITE_BRAND} · ${persona.label} à ${cityLabel}`,
        "url": `${SITE_URL}/pour/${persona.slug}/${city}`,
        "description": `Sécurisation des travaux BTP pour ${persona.label.toLowerCase()} à ${cityLabel}.`,
        "areaServed": { "@type": "City", "name": cityLabel },
        "priceRange": "€€",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Accueil", "item": SITE_URL },
          { "@type": "ListItem", "position": 2, "name": "Pour qui ?", "item": `${SITE_URL}/pour-qui` },
          { "@type": "ListItem", "position": 3, "name": persona.label, "item": `${SITE_URL}/pour/${persona.slug}` },
          { "@type": "ListItem", "position": 4, "name": cityLabel, "item": `${SITE_URL}/pour/${persona.slug}/${city}` },
        ],
      },
    ],
  };
}

export default function PersonaCityPage() {
  const { persona: pSlug, city } = useParams();
  const p = PERSONA_TYPES[pSlug];
  const [cityData, setCityData] = useState(null);
  const [cityError, setCityError] = useState(false);

  useEffect(() => {
    if (city) {
      apiClient
        .get(`/public/cities/${city}`)
        .then(({ data }) => setCityData(data))
        .catch(() => setCityError(true));
    }
  }, [city]);

  if (!p) return <Navigate to="/pour-qui" replace />;
  if (cityError) return <Navigate to={`/pour/${pSlug}`} replace />;

  const cityLabel = cityData?.city_label || (city.charAt(0).toUpperCase() + city.slice(1));

  return (
    <div className="min-h-screen bg-[#F7F7F9]" data-testid={`persona-city-${pSlug}-${city}`}>
      <SEO cfg={buildCrossSEO(p, city, cityLabel)} />
      <Navbar />

      <section className="bg-[#0A0A0B] text-white border-b-2 border-[#0A0A0B] relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-10 pointer-events-none" />
        <div className="max-w-6xl mx-auto px-6 py-16 lg:py-20 relative">
          <nav className="mono text-[11px] uppercase tracking-widest text-zinc-400 mb-5" aria-label="Fil d'ariane">
            <Link to="/" className="hover:text-[#FF4D00]">Accueil</Link>
            <span className="mx-2">/</span>
            <Link to="/pour-qui" className="hover:text-[#FF4D00]">Pour qui ?</Link>
            <span className="mx-2">/</span>
            <Link to={`/pour/${p.slug}`} className="hover:text-[#FF4D00]">{p.label}</Link>
            <span className="mx-2">/</span>
            <span className="text-[#FF4D00]">{cityLabel}</span>
          </nav>
          <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00] font-semibold">// {p.label} · {cityLabel}</div>
          <h1 className="display text-4xl lg:text-6xl tracking-tighter mt-3 max-w-4xl leading-[0.95]">
            {p.hero.h1.split(" ").slice(0, 3).join(" ")} à <span className="text-[#FF4D00]">{cityLabel}</span>
          </h1>
          <p className="mt-6 text-zinc-300 text-base lg:text-lg max-w-3xl">
            {p.hero.lead} Nos artisans SIRET vérifiés à {cityLabel} et alentours interviennent sur vos chantiers en toute sécurité.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to={`${p.ctaLink}&ville=${city}`}>
              <button className="btn-batisur flex items-center gap-2" data-testid={`persona-city-cta-${pSlug}-${city}`}>
                {p.ctaTitle} à {cityLabel} <ArrowRight className="w-4 h-4" />
              </button>
            </Link>
            <Link to={`/artisans/ville/${city}`}>
              <button className="btn-anthracite border-2 border-white/40">
                Voir les artisans de {cityLabel}
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Trust protections local */}
      <section className="max-w-6xl mx-auto px-6 py-16 border-b-2 border-[#0A0A0B]">
        <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00] font-semibold">// 3 protections à {cityLabel}</div>
        <h2 className="display text-3xl lg:text-5xl tracking-tighter mt-3 max-w-3xl leading-[0.95]">
          Vos chantiers à {cityLabel}, <span className="text-[#FF4D00]">100% sécurisés.</span>
        </h2>
        <div className="mt-10 grid md:grid-cols-3 gap-5">
          {[
            { i: Lock, t: "Séquestre Stripe", d: `Fonds pour vos chantiers à ${cityLabel} bloqués chez Stripe (agréé bancaire européen).` },
            { i: Camera, t: "Preuves horodatées", d: `Chaque étape à ${cityLabel} prouvée par photo GPS + timestamp SHA-256 opposable.` },
            { i: BadgeCheck, t: "SIRET vérifié Sirene", d: `Artisans de ${cityLabel} contrôlés en temps réel via l'API Sirene/INSEE.` },
          ].map(({ i: Icon, t, d }, idx) => (
            <div key={idx} className="border-2 border-[#0A0A0B] bg-white p-6 shadow-[3px_3px_0_#0A0A0B]">
              <div className="w-12 h-12 border-2 border-[#0A0A0B] bg-[#FF4D00] flex items-center justify-center mb-4">
                <Icon className="w-6 h-6 text-white" strokeWidth={2.5} />
              </div>
              <div className="display text-lg leading-tight">{t}</div>
              <p className="text-sm text-zinc-700 mt-2">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Benefits (6 H3) with city context */}
      <section className="bg-white border-b-2 border-[#0A0A0B]">
        <div className="max-w-6xl mx-auto px-6 py-16 lg:py-20">
          <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00] font-semibold">// {p.label} à {cityLabel}</div>
          <h2 className="display text-3xl lg:text-5xl tracking-tighter mt-3 max-w-3xl leading-[0.95]">
            Fonctionnalités dédiées {p.label.toLowerCase()} de <span className="text-[#FF4D00]">{cityLabel}</span>
          </h2>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {p.benefits.map((b, i) => (
              <div key={i} className="border-l-4 border-[#FF4D00] pl-5 py-1">
                <h3 className="display text-xl leading-tight">{b.h3}</h3>
                <p className="text-sm text-zinc-700 mt-2 leading-relaxed">{b.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[#FF4D00] text-white border-b-2 border-[#0A0A0B]">
        <div className="max-w-5xl mx-auto px-6 py-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
          <div>
            <div className="mono text-[11px] uppercase tracking-widest text-white/90 font-semibold">// {cityLabel} · Prêt ?</div>
            <h2 className="display text-3xl lg:text-4xl tracking-tighter mt-2 leading-tight">
              {p.label} · Lancez votre projet à {cityLabel}
            </h2>
          </div>
          <Link to={`${p.ctaLink}&ville=${city}`}>
            <button className="btn-anthracite shadow-[4px_4px_0_#0A0A0B] flex items-center gap-2">
              {p.ctaTitle} <ArrowRight className="w-4 h-4" />
            </button>
          </Link>
        </div>
      </section>

      {/* Prospect notify (only if city is prospected) */}
      {cityData?.prospected && (
        <section className="bg-[#F7F7F9] border-b-2 border-[#0A0A0B]">
          <div className="max-w-4xl mx-auto px-6 py-14">
            <CityProspectForm
              citySlug={city}
              cityLabel={cityLabel}
              context="persona"
              personaSlug={p.slug}
            />
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
